// status.schema.json `payload.progress` (progress panel, CLI 3.41 — plan
// 20261020-1117 §Hợp đồng). Absent on CLIs ≤ 3.40. Every object is a
// `looseObject` (B13): a new optional field / enum value never bumps
// `schema_version` and never breaks an older reader; changing a field's
// meaning/type or removing one does. Enum-valued fields are OPEN (`openEnum`:
// known values listed, any string accepted) so a validator built from an older
// status.schema.json still accepts a newer CLI's new value (B13). TS twin:
// ../status-progress-types.ts (closed unions for the CLI's own code).
import { z } from "zod";
import {
  HUMAN_ACTION_KINDS,
  JOURNEY_STATES,
  JOURNEY_STEP_IDS,
  WORKER_STATES,
} from "../status-progress-types.js";

/** Known values first (documented in the generated JSON Schema), any other string still valid. */
function openEnum<const T extends readonly [string, ...string[]]>(values: T) {
  return z.union([z.enum(values), z.string()]);
}

const AttemptSchema = z.looseObject({
  attempt_no: z.number().int(),
  run_id: z.string(),
  session_id: z.string().optional(),
  started_at: z.string().optional(),
  finished_at: z.string().optional(),
  category: z.string().optional(),
});

const TicketSchema = z.looseObject({
  id: z.string(),
  title: z.string(),
  kind: z.string(),
  slice_id: z.string(),
  status: z.string(),
  depends_on: z.array(z.string()),
  adapter: z.string(),
  executor: z.string(),
  agent_hints: z.array(z.string()),
  skill_hints: z.array(z.string()),
  playbook: z.string().optional(),
  /** Finished runs only — the in-flight run is `current_attempt.attempt_no`. */
  physical_runs: z.number().int().nonnegative(),
  counted_failures: z.number().int().nonnegative(),
  /** Ceiling of COUNTED failures, not of runs. */
  max_counted_failures: z.number().int().optional(),
  started_at: z.string().optional(),
  finished_at: z.string().optional(),
  cost_usd: z.number().optional(),
  last_verdict_category: z.string().optional(),
  last_verdict_reasons: z.array(z.string()),
  recovery_of: z.string().optional(),
  /**
   * Review candidate T-9001/T-9002 — no `.avatar/tickets` file (beta.2). The TS
   * type says `true`, but `z.boolean()` is deliberate: the generated pack schema
   * (templates/autopilot/status.schema.json) is `{"type":"boolean"}`, and the
   * twin must stay byte-parity with it — do not "tighten" this to `z.literal(true)`.
   */
  controller_reserved: z.boolean().optional(),
  attempts: z.array(AttemptSchema),
});

export const ProgressSchema = z.looseObject({
  schema_version: z.literal(1),
  generated_at: z.string(),
  mission: z.looseObject({
    id: z.string(),
    started_at: z.string().optional(),
    status_label: z.string().optional(),
    status_label_stale: z.boolean().optional(),
    supervisor_alive: z.boolean(),
    /** ABORTED ∨ released ∨ done_without_deploy ∨ (done ∧ no release config). */
    terminal: z.boolean(),
    /** First step not done/skipped; absent when every step is. */
    active_stage: openEnum(JOURNEY_STEP_IDS).optional(),
  }),
  journey: z.array(
    z.looseObject({
      id: openEnum(JOURNEY_STEP_IDS),
      state: openEnum(JOURNEY_STATES),
      started_at: z.string().optional(),
      ended_at: z.string().optional(),
      summary: z.string().optional(),
      source: z.string(),
    }),
  ),
  tickets: z.array(TicketSchema),
  /** Ticket files unreadable (e.g. `duplicate_ticket_id`) — `tickets` is then []. */
  tickets_error: z.string().optional(),
  current_attempt: z
    .looseObject({
      ticket_id: z.string(),
      attempt_no: z.number().int(),
      run_id: z.string(),
      session_id: z.string().optional(),
      started_at: z.string().optional(),
      executor: z.string(),
      worker_pid: z.number().int().positive().optional(),
      worker_state: openEnum(WORKER_STATES),
    })
    .optional(),
  human_actions: z.array(
    z.looseObject({
      kind: openEnum(HUMAN_ACTION_KINDS),
      gate: z.string().optional(),
      request_id: z.string().optional(),
      question_id: z.string().optional(),
      ticket_id: z.string().optional(),
      blocked_by: z.string().optional(),
      recovery_skipped_reason: z.string().optional(),
      text: z.string(),
      /** Sentence to say to Claude in chat (beta.2). */
      hint_chat: z.string().optional(),
      hint_cli: z.string().optional(),
    }),
  ),
});
